import React, { useState, useEffect, useMemo } from 'react';
import InfrastructureCanvasViewer from './InfrastructureCanvasViewer';
import { GitHubService } from '../../code';
import {
  fetchManifest,
  fetchArtifactFile,
  filterEntriesByKind,
  getSandboxBaseUrl
} from '../../code/services/sandboxArtifactService';
import TerraformParser from '../../../../../shared/lib/terraformParser';
import VishCoderChatPanel from '../../../channels/chat/components/VishCoderChatPanel';
import { getProjectGitSettingsFromStorage } from '../../../../../shared/lib/storageUtils';

interface ArchitectureTabProps {
    projectId: number | string | null;
    refreshTrigger?: number;
    isRightPanelOpen?: boolean;
    onToggleRightPanel?: () => void;
    source?: 'git' | 'sandbox';
    userId?: string | null;
    canEdit?: boolean;
}

interface GitHubSettings {
    repo_url: string;
    branch: string;
    token?: string;
}



const ArchitectureTab: React.FC<ArchitectureTabProps> = ({ 
    projectId, 
    refreshTrigger = 0,
    isRightPanelOpen = false,
    onToggleRightPanel,
    source = 'git',
    userId = null,
    canEdit = true
}) => {
    const [githubSettings, setGitHubSettings] = useState<GitHubSettings | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showConfigureMessage, setShowConfigureMessage] = useState(false);
    const [manifestError, setManifestError] = useState<string | null>(null);

    const [terraformFiles, setTerraformFiles] = useState<Array<{ name: string; path: string; selected: boolean; content?: string }>>([]);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [internalRefreshTrigger, setInternalRefreshTrigger] = useState(0);
    const [parsedTerraformResources, setParsedTerraformResources] = useState<any[]>([]);
    const [groupedTerraformResources, setGroupedTerraformResources] = useState<any>({});

    const baseUrl = useMemo(() => getSandboxBaseUrl(userId, projectId), [userId, projectId]);


    // Load project-level GitHub settings (only for Git mode)
    useEffect(() => {
        if (source === 'sandbox') {
            setShowConfigureMessage(false);
            return;
        }
        const savedSettings = getProjectGitSettingsFromStorage(projectId);
        if (savedSettings && savedSettings.repo_url && savedSettings.branch) {
            setGitHubSettings({
                repo_url: savedSettings.repo_url,
                branch: savedSettings.branch,
                token: savedSettings.token
            });
            setShowConfigureMessage(false);
        } else {
            setShowConfigureMessage(true);
        }
    }, [source, projectId]);

    // Handle refresh trigger from parent
    useEffect(() => {
        if (refreshTrigger > 0) {
            setInternalRefreshTrigger(prev => prev + 1);
        }
    }, [refreshTrigger]);

    // Unified infrastructure data loading - works for both Git and Sandbox modes
    useEffect(() => {
        if (source === 'git' && !githubSettings) return;
        if (source === 'sandbox' && !baseUrl) return;

        const loadInfrastructureData = async () => {
            setIsLoading(true);
            setError(null);
            setManifestError(null);

            try {
                let terraformFiles: Array<{ name: string; path: string; content: string }> = [];

                // Fetch Terraform files based on source
                if (source === 'git' && githubSettings) {
                    // Git mode: Fetch from GitHub
                    const repoInfo = GitHubService.parseRepoPath(githubSettings.repo_url);
                    if (!repoInfo) {
                        throw new Error(`Invalid repository format: "${githubSettings.repo_url}". Please use the format: owner/repository`);
                    }

                    terraformFiles = await getTerraformFilesFromRepo(
                        repoInfo.owner,
                        repoInfo.repo,
                        githubSettings.branch
                    );

                } else if (source === 'sandbox' && baseUrl) {
                    // Sandbox mode: Fetch from manifest
                    const manifestCacheKey = `sandbox_manifest_${baseUrl}`;
                    let manifest = null;
                    
                    // Check cache
                    const cached = sessionStorage.getItem(manifestCacheKey);
                    if (cached) {
                        try {
                            manifest = JSON.parse(cached);
                        } catch (e) {
                            // Invalid cache, ignore
                        }
                    }

                    if (!manifest) {
                        manifest = await fetchManifest(baseUrl);
                        sessionStorage.setItem(manifestCacheKey, JSON.stringify(manifest));
                    }

                    // Filter entries for architecture files (arch kind, or .tf files)
                    const archEntries = filterEntriesByKind(manifest.entries, 'arch');
                    const tfEntries = manifest.entries.filter((e: any) => e.path.endsWith('.tf'));
                    const allArchEntries = [...archEntries, ...tfEntries.filter((e: any) => !archEntries.includes(e))];

                    if (allArchEntries.length === 0) {
                        throw new Error('No architecture files found in sandbox artifacts');
                    }

                    // Fetch all Terraform files (filter .tf files)
                    const tfFiles = allArchEntries.filter(e => e.path.endsWith('.tf'));

                    for (const entry of tfFiles) {
                        try {
                            const content = await fetchArtifactFile(baseUrl, entry.path);
                            terraformFiles.push({
                                name: entry.path.split('/').pop() || entry.path,
                                path: entry.path,
                                content: content
                            });
                        } catch (fileError) {
                            console.warn(`Failed to fetch ${entry.path}:`, fileError);
                        }
                    }

                    if (terraformFiles.length === 0) {
                        throw new Error('No Terraform files could be loaded from sandbox artifacts');
                    }
                }

                // Common processing for both modes: Parse and group Terraform files
                if (terraformFiles.length === 0) {
                    throw new Error('No Terraform files found');
                }

                // Set the Terraform files list for the sidebar
                const fileList = terraformFiles.map((file, index) => ({
                    name: file.name,
                    path: file.path,
                    selected: index === 0,
                    content: file.content
                }));
                setTerraformFiles(fileList);
                setSelectedFile(terraformFiles[0]?.path || null);

                // Parse Terraform files using TerraformParser (same for both modes)
                const parsedResources = TerraformParser.parseGitHubTerraformFiles(terraformFiles);
                console.log(`Parsed resources from ${source}:`, parsedResources);
                
                // Store parsed resources for advanced grouping/filtering and architecture visualization
                setParsedTerraformResources(parsedResources);
                
                // Get grouped resources from cache (automatically populated by parseGitHubTerraformFiles)
                const cacheKey = TerraformParser.generateCacheKey(terraformFiles);
                const groupedResources = TerraformParser.getCachedGroupedResources(cacheKey);
                console.log('Grouped resources from cache:', groupedResources);
                
                // Store grouped resources for use in canvas
                setGroupedTerraformResources(groupedResources);

            } catch (err) {
                console.error(`Failed to load infrastructure data from ${source}:`, err);
                const errorMessage = err instanceof Error ? err.message : `Failed to load infrastructure data from ${source}`;
                
                // Set manifestError for sandbox mode manifest failures
                if (source === 'sandbox' && (errorMessage.includes('manifest') || errorMessage.includes('No architecture files'))) {
                    setManifestError(errorMessage);
                }
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        loadInfrastructureData();
    }, [githubSettings, baseUrl, internalRefreshTrigger, source]);

    // Get Terraform files from GitHub repository using GitHubService
    const getTerraformFilesFromRepo = async (owner: string, repo: string, branch: string): Promise<any[]> => {
        try {
            const terraformFiles: any[] = [];
            
            // Recursively search for .tf files in all directories (using existing pattern)
            const searchTerraformFilesRecursively = async (path: string = ''): Promise<void> => {
                try {
                    const token = githubSettings?.token?.trim() || undefined;
                    const contents = await GitHubService.getContents(owner, repo, path, branch, token);
                    
                    for (const item of contents) {
                        if (item.type === 'file' && item.name.endsWith('.tf')) {
                            // Simple filter: exclude variable/output files like the backup code does
                            const fileName = item.name.toLowerCase();
                            const isVariableFile = fileName === 'variables.tf' || fileName === 'terraform-variables.tf';
                            const isOutputFile = fileName === 'outputs.tf' || fileName === 'output.tf' || fileName === 'terraform-outputs.tf';
                            
                            if (!isVariableFile && !isOutputFile) {
                                // Get file content using GitHubService
                                try {
                                    const token = githubSettings?.token?.trim() || undefined;
                                    const content = await GitHubService.getRawFileContent(owner, repo, item.path, branch, token);
                                    terraformFiles.push({
                                        name: item.name,
                                        path: item.path,
                                        content: content
                                    });
                                    console.log(`Found Terraform file: ${item.path}`);
                                } catch (contentError) {
                                    console.warn(`Failed to get content for ${item.path}:`, contentError);
                                }
                            } else {
                                console.log(`Skipping: ${item.path} (variable/output file)`);
                            }
                        } else if (item.type === 'dir') {
                            // Recursively search subdirectories
                            await searchTerraformFilesRecursively(item.path);
                        }
                    }
                } catch (error) {
                    console.warn(`Failed to search directory ${path}:`, error);
                    // Continue searching other directories even if one fails
                }
            };
            
            await searchTerraformFilesRecursively();
            return terraformFiles;
            
        } catch (error) {
            console.error('Error fetching Terraform files:', error);
            throw error;
        }
    };

    // TerraformParser now handles all parsing logic - no need for duplicate functions

    // Handle Terraform file selection
    const handleTerraformFileSelect = async (filePath: string) => {
        console.log('Selected Terraform file:', filePath);
        
        // Update selected file
        setTerraformFiles(prev => prev.map(file => ({
            ...file,
            selected: file.path === filePath
        })));
        setSelectedFile(filePath);
        
        // Reload infrastructure data for the selected file
        if (githubSettings) {
            try {
                const repoInfo = GitHubService.parseRepoPath(githubSettings.repo_url);
                if (repoInfo) {
                    const fileContent = await getTerraformFileContent(
                        repoInfo.owner,
                        repoInfo.repo,
                        filePath,
                        githubSettings.branch
                    );
                    
                    if (fileContent) {
                        try {
                            // Use enhanced TerraformParser to parse the content
                            const parsedData = TerraformParser.parseTerraformConfig(fileContent, filePath);
                            

                        } catch (parseError) {
                            console.warn(`Failed to parse Terraform file ${filePath}:`, parseError);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading selected file:', error);
            }
        }
    };



    // Get content of a specific Terraform file using GitHubService
    const getTerraformFileContent = async (owner: string, repo: string, filePath: string, branch: string): Promise<string | null> => {
        try {
            const token = githubSettings?.token?.trim() || undefined;
            const content = await GitHubService.getRawFileContent(owner, repo, filePath, branch, token);
            return content;
        } catch (error) {
            console.error('Error fetching file content:', error);
            return null;
        }
    };



    const renderConfigureMessage = () => {
        return (
            <div className="h-full w-full flex items-center justify-center p-8">
                <div className="text-center max-w-md mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center border border-white/10 backdrop-blur-lg">
                        <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-4">Infrastructure Architecture Canvas</h3>
                    <p className="text-gray-300 mb-8 leading-relaxed">
                        View your infrastructure architecture using an interactive Canvas, built from Terraform (.tf) files in your GitHub repository.
                    </p>
                    
                    <div className="bg-white/5 backdrop-blur-lg rounded-xl p-4 border border-white/10 mb-6">
                        <p className="text-sm text-gray-400">
                            Go to <strong className="text-white">Settings → GitHub Repository</strong> and enter your repository path and branch.
                        </p>
                    </div>
                    
                    <div className="space-y-4 text-sm text-gray-400">
                        <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                                <strong className="text-white">Terraform Files:</strong> Automatically detects and parses .tf files
                            </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                                <strong className="text-white">Interactive Canvas:</strong> Drag, zoom, and explore your infrastructure
                            </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                                <strong className="text-white">Resource Visualization:</strong> Beautiful, organized view of AWS resources
                            </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                                <strong className="text-white">Real-time Updates:</strong> Reflects changes in your Terraform files
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderError = (errorMessage: string) => {
        return (
            <div className="h-full w-full flex items-center justify-center p-8">
                <div className="text-center max-w-md mx-auto">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center border border-red-400/20">
                        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Error Loading Infrastructure</h3>
                    <p className="text-gray-300 mb-4">{errorMessage}</p>
                    <div className="text-sm text-gray-400">
                        Check your GitHub repository settings in <strong className="text-white">Settings → GitHub Repository</strong>
                    </div>
                </div>
            </div>
        );
    };

    const renderInfrastructureCanvas = () => {
        // Render canvas if we have resources loaded (works for both Git and Sandbox modes)
        if (parsedTerraformResources.length === 0) return null;

        return (
            <div className="h-full">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="text-sm font-medium text-white">
                            {source === 'sandbox' 
                                ? `Sandbox Artifacts${baseUrl ? ` (${baseUrl.replace('https://', '').replace('.sandbox.vishmaker.com', '')})` : ''}`
                                : githubSettings 
                                    ? `${githubSettings.repo_url} (${githubSettings.branch})`
                                    : 'Infrastructure'
                            }
                        </span>
                    </div>
                    <div className="text-xs text-gray-400">
                        {parsedTerraformResources.length} resources loaded
                    </div>
                </div>
                <div className="h-full">
                    <InfrastructureCanvasViewer
                        projectId={projectId?.toString()}
                        onNodeClick={(nodeId, data) => console.log('Node clicked:', nodeId, data)}
                        onNodeEdit={(nodeId, data) => console.log('Node edit:', nodeId, data)}
                        terraformFiles={terraformFiles}
                        onTerraformFileSelect={handleTerraformFileSelect}
                        parsedTerraformResources={parsedTerraformResources}
                        groupedTerraformResources={groupedTerraformResources}
                    />
                </div>
            </div>
        );
    };

    if (manifestError) {
        const isNoFilesError = manifestError.includes('No architecture files found in sandbox artifacts');
        return (
            <div className="h-full w-full flex items-center justify-center p-8">
                <div className="text-center max-w-md mx-auto">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border ${isNoFilesError ? 'bg-amber-500/20 border-amber-400/20' : 'bg-red-500/20 border-red-400/20'}`}>
                        <svg className={`w-8 h-8 ${isNoFilesError ? 'text-amber-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                        {isNoFilesError ? 'NO Architecture Files Found in Sandbox Artifacts' : 'Error Loading Manifest'}
                    </h3>
                    <p className="text-gray-300 mb-4">{isNoFilesError ? 'Retry after adding architecture or Terraform files to the sandbox.' : manifestError}</p>
                    <button
                        onClick={() => {
                            if (baseUrl) {
                                sessionStorage.removeItem(`sandbox_manifest_${baseUrl}`);
                            }
                            setInternalRefreshTrigger(prev => prev + 1);
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (showConfigureMessage && source === 'git') {
        return renderConfigureMessage();
    }

    if (error && !manifestError) {
        return renderError(error);
    }

    if (isLoading) {
        return (
            <div className="h-full w-full flex items-center justify-center p-8">
                <div className="text-center max-w-md mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center border border-white/10 backdrop-blur-lg">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-400"></div>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-4">Loading Infrastructure</h3>
                    <p className="text-gray-300">Parsing Terraform files{source === 'sandbox' ? ' from sandbox artifacts' : ' from GitHub'}...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {parsedTerraformResources.length > 0 ? (
                renderInfrastructureCanvas()
            ) : (
                // Fallback state
                <div className="h-full w-full flex items-center justify-center p-8">
                    <div className="text-center max-w-md mx-auto">
                        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center border border-white/10 backdrop-blur-lg">
                            <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-4">No Infrastructure Data</h3>
                        <p className="text-gray-300">
                            {source === 'sandbox' 
                                ? 'No architecture files found in sandbox artifacts.'
                                : 'Configure your GitHub repository to view infrastructure architecture.'
                            }
                        </p>
                    </div>
                </div>
            )}
            {/* VishCoder Chat Panel */}
            {onToggleRightPanel && (
                <VishCoderChatPanel 
                    isOpen={isRightPanelOpen} 
                    onToggle={onToggleRightPanel}
                    page="architecture"
                    projectId={projectId}
                />
            )}
        </>
    );
};

export default ArchitectureTab; 